/**
 * internal-accounts-api — typed, bound wrappers for the Internal Accounts RPCs
 * and edge functions. Always call `client.rpc(...)` / `client.functions.invoke(...)`
 * on the client object (never detached) so `this` stays bound.
 * internal_profiles is read-only from the browser; writes go through RPCs / edge functions.
 */
import { supabase } from '@/integrations/supabase/client';

export interface InternalAccountRow {
  user_id: string;
  display_name: string;
  auth_email: string;
  role: string;
  department: string | null;
  phone: string | null;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string | null;
  access_preset?: string | null;
}

interface RpcResult { data: unknown; error: { message: string } | null }
interface LooseClient {
  rpc: (fn: string, args?: Record<string, unknown>) => PromiseLike<RpcResult>;
  from: (table: string) => {
    select: (cols: string) => {
      eq: (col: string, val: string) => { maybeSingle: () => PromiseLike<{ data: { is_active: boolean } | null; error: { message: string } | null }> };
    };
  };
}
const client = supabase as unknown as LooseClient;
const PORTAL_HEADERS = { 'X-Portal': 'admin' };

async function callRpc<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await client.rpc(fn, args);
  if (error) throw new Error(error.message || `${fn} failed`);
  return data as T;
}

async function callFn<T = unknown>(fn: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(fn, { body, headers: PORTAL_HEADERS });
  const payloadErr = (data as { error?: unknown } | null)?.error;
  if (payloadErr) throw new Error(typeof payloadErr === 'string' ? payloadErr : JSON.stringify(payloadErr));
  if (error) {
    const ctx = (error as { context?: Response }).context;
    let msg = error.message;
    if (ctx && typeof ctx.text === 'function') {
      try {
        const txt = await ctx.text();
        try { const j = JSON.parse(txt); msg = j?.error ?? j?.message ?? txt; } catch { msg = txt || msg; }
      } catch { /* body already read */ }
    }
    throw new Error(msg || `${fn} failed`);
  }
  return data as T;
}

export const internalAccountsApi = {
  listRoles: () => callRpc<string[]>('admin_list_internal_roles').then(r => r ?? []),
  listAccounts: () => callRpc<InternalAccountRow[]>('admin_list_internal_accounts').then(r => r ?? []),
  updateAccount: (i: { user_id: string; display_name: string; department: string; phone: string; role: string | null }) =>
    callRpc<void>('admin_update_internal_account', {
      p_user_id: i.user_id, p_display_name: i.display_name,
      p_department: i.department, p_phone: i.phone, p_role: i.role,
    }),
  setActiveRpc: (p_user_id: string, p_is_active: boolean) =>
    callRpc<void>('admin_set_internal_active', { p_user_id, p_is_active }),
  touchLastLogin: () => callRpc<void>('internal_touch_last_login'),
  /** Own-row status read (SELECT only). null = no internal profile row. */
  getOwnActive: async (userId: string): Promise<boolean | null> => {
    const { data, error } = await client.from('internal_profiles').select('is_active').eq('user_id', userId).maybeSingle();
    if (error || !data) return null;
    return data.is_active;
  },
  /** Own display name + role (SELECT only, own-row policy). null = no row. */
  getOwnProfile: async (): Promise<{ display_name: string; role: string } | null> => {
    const { data: u } = await supabase.auth.getUser();
    const uid = u.user?.id;
    if (!uid) return null;
    const { data, error } = await (client.from('internal_profiles') as unknown as {
      select: (c: string) => { eq: (k: string, v: string) => { maybeSingle: () => PromiseLike<{ data: { display_name: string; role: string } | null; error: unknown }> } };
    }).select('display_name, role').eq('user_id', uid).maybeSingle();
    if (error || !data) return null;
    return data;
  },
  createUser: (i: { email: string; display_name: string; role: string; department?: string | null; phone?: string | null; password: string }) =>
    callFn('admin-create-internal-user', i),
  setActive: (i: { user_id: string; is_active: boolean }) => callFn('admin-set-internal-active', i),
  resetPassword: (i: { user_id: string; new_password: string }) => callFn('admin-reset-internal-password', i),
  deleteUser: (i: { user_id: string }) => callFn('admin-delete-internal-user', i),
};
